import { SeasonPanel } from "./SeasonPanel";
import { ShopPanel } from "./ShopPanel";
import { SupplyPanel } from "./SupplyPanel";
import type { SupplyId } from "@frontier/content/supplies";
import { hasIncompleteTutorial } from "@frontier/content/contract-orders";
import { useEffect, useMemo, useRef, useState } from "react";
import { currentRisk, deathMessage } from "./risk-ui";
import { findAttackableTarget, physicalRegion } from "./targeting";
import { galaxyRegionAt } from "./galaxy-navigation";
import type { GameController } from "./game/mount-game";
import type { NavigationState } from "./game/navigation-types";
import type { Point } from "./game/geometry";
import { GameLayout } from "./GameLayout";
import { GameViewport } from "./GameViewport";
import { Hud } from "./Hud";
import { Hangar } from "./Hangar";
import { GalaxyMap } from "./GalaxyMap";
import { useGalaxyCourse } from "./use-galaxy-course";
import type { GalaxyTarget } from "./galaxy-navigation";
import { Feedback } from "./Feedback";
import { AccountPanel } from "./AccountPanel";
import { HelpPanel } from "./HelpPanel";
import { useBackgroundMotion } from "./use-background-motion";
import { ChatPanel } from "./ChatPanel";
import { STANDALONE, type GamePlatform } from "./platform-launch";
import { useGameSession } from "./use-game-session";
import { useGameAudio } from "./use-game-audio";
import { useVisualViewport } from "./use-visual-viewport";
import { hasTutorialGuidance } from "./onboarding-ui";
import "./flight-layout.css";
import "./contract-roster.css";
import {
  eventNavigationCaption,
  publicEventObjective,
  trackedPublicEvent,
} from "./event-navigation";

export function App({
  platform = STANDALONE,
}: { platform?: GamePlatform } = {}) {
  const game = useGameSession(platform);
  const requiresFaction =
    game.profile?.factionId === null &&
    game.profile.tutorial?.route !== "training";
  const flightController = useRef<GameController | null>(null);
  const backgroundMotion = useBackgroundMotion((point) =>
    flightController.current?.setBackdropTilt(point),
  );
  const [flightReady, setFlightReady] = useState(false);
  const [navigation, setNavigation] = useState<NavigationState>({
    status: "idle",
    reason: null,
  });
  const viewport = useVisualViewport();
  const [boost, setBoost] = useState(false);
  useEffect(
    () => setBoost(false),
    [viewport.width, viewport.height, viewport.left, viewport.top],
  );
  const [targetId, setTargetId] = useState("");
  const audio = useGameAudio({
    world: game.world,
    worldReceivedAt: game.worldReceivedAt,
    playerId: game.profile?.playerId ?? "",
    runId: game.profile?.runId ?? "",
    connected: game.status === "connected",
    targetId,
  });
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [cargoOpen, setCargoOpen] = useState(false);
  const [selectedSupplyId, setSelectedSupplyId] = useState<SupplyId>("battery");
  useEffect(() => setSelectedSupplyId("battery"), [game.profile?.playerId]);
  const openAccount = () => {
    setHelpOpen(false);
    setCargoOpen(false);
    setAccountOpen(true);
  };
  const openHelp = () => {
    setAccountOpen(false);
    setCargoOpen(false);
    setChatOpen(false);
    setGalaxyOpen(false);
    setFeedbackOpen(false);
    setHelpOpen(true);
  };
  const chatUnread = Object.values(game.chat.channels).reduce(
    (sum, channel) => sum + channel.unread,
    0,
  );
  const openChat = () => {
    setTargetId("");
    setBoost(false);
    setGalaxyOpen(false);
    setChatOpen(true);
    setCargoOpen(false);
  };
  const [galaxyOpen, setGalaxyOpen] = useState(false);
  const pilot = game.world?.players.find(
    (item) => item.id === game.profile?.playerId,
  );
  const friends = useMemo(
    () => game.party.party?.members.map((member) => member.playerId) ?? [],
    [game.party.party],
  );
  const risk = currentRisk(game.risk, game.profile?.runId);
  const shownLoss = useRef("");
  useEffect(() => {
    const id = risk?.state.lastLoss?.deathId;
    if (id && (!pilot?.dead || risk?.state.dead) && shownLoss.current !== id) {
      shownLoss.current = id;
      game.setNotice(deathMessage(risk, { dead: false, pending: false }) ?? "");
    }
  }, [risk, pilot?.dead, game.setNotice]);
  const docked = pilot?.docked === true;
  const flightBlocked =
    requiresFaction ||
    feedbackOpen ||
    shopOpen ||
    accountOpen ||
    helpOpen ||
    galaxyOpen ||
    cargoOpen ||
    docked ||
    pilot?.dead === true ||
    game.status !== "connected";
  useEffect(() => {
    if (flightBlocked) setBoost(false);
  }, [flightBlocked]);
  const galaxyCourse = useGalaxyCourse({
    playerId: game.profile?.playerId ?? "",
    runId: game.profile?.runId ?? "",
    sessionKey: game.auth.session?.token ?? "",
    world: game.world, pilot, receivedAt: game.worldReceivedAt, gateFailure: game.gateFailure, connected: game.status === "connected",
    ready: flightReady, blocked: flightBlocked, controller: flightController,
    onJump: (gateId, riskAcknowledged) => game.action("jump", undefined, gateId, riskAcknowledged),
  });
  const navigate = (point: Point) => {
    if (!flightBlocked) {
      galaxyCourse.cancel();
      flightController.current?.navigate(point);
    }
  };
  const navigationChanged = (state: NavigationState) => {
    setNavigation(state);
    galaxyCourse.navigationChanged(state);
    if (state.status === "idle" && state.reason === "outside-region")
      game.setNotice("В другую область — через врата.");
    if (state.status === "idle" && state.reason === "stale")
      game.setNotice("Курс остановлен: ждём свежий сигнал сектора.");
  };
  const mission =
    game.mission?.runId === game.profile?.runId ? game.mission : null;
  const event = game.world
    ? (trackedPublicEvent(game.world, game.events) ?? null)
    : null;
  const eventObjective =
    game.world && pilot && event && game.navigationSource === "event"
      ? publicEventObjective(game.world, pilot, event)
      : null;
  const eventNavigation =
    event && eventObjective
      ? {
          ...eventObjective,
          name: eventNavigationCaption(event, eventObjective),
        }
      : null;
  const joinEvent = (eventId: string) => {
    setGalaxyOpen(false);
    galaxyCourse.cancel();
    setTargetId("");
    setBoost(false);
    game.joinEvent(eventId);
  };
  const regionId = pilot ? game.world?.boundaryRules ? galaxyRegionAt(game.world, pilot)?.id : physicalRegion(pilot) : undefined;
  const openGalaxy = () => {
    setTargetId("");
    setBoost(false);
    setGalaxyOpen(true);
    setCargoOpen(false);
  };
  const navigateFromGalaxy = (target: GalaxyTarget) => {
    setGalaxyOpen(false);
    if (
      !game.profile ||
      !pilot ||
      !regionId ||
      pilot.docked ||
      pilot.dead ||
      pilot.pending ||
      game.status !== "connected"
    )
      return;
    galaxyCourse.plot(target);
  };
  useEffect(() => {
    setGalaxyOpen(false);
    galaxyCourse.cancel();
    setTargetId("");
    setBoost(false);
    setFeedbackOpen(false);
    setHelpOpen(false);
    setChatOpen(false);
    setCargoOpen(false);
  }, [game.profile?.runId, game.auth.session?.token]);
  useEffect(() => {
    if (game.status !== "connected" || pilot?.dead) {
      setShopOpen(false);
      setGalaxyOpen(false);
      galaxyCourse.cancel();
      setCargoOpen(false);
    }
  }, [game.status, pilot?.dead]);
  const previousRegion = useRef(regionId);
  useEffect(() => {
    if (previousRegion.current && previousRegion.current !== regionId) {
      setTargetId("");
      setBoost(false);
    }
    previousRegion.current = regionId;
  }, [regionId]);
  useEffect(() => {
    if (
      targetId &&
      (!game.world ||
        !pilot ||
        !findAttackableTarget(game.world, pilot, targetId, friends))
    )
      setTargetId("");
  }, [game.world, pilot, targetId, friends]);
  useEffect(() => {
    const release = () => setBoost(false);
    window.addEventListener("blur", release);
    document.addEventListener("visibilitychange", release);
    return () => {
      window.removeEventListener("blur", release);
      document.removeEventListener("visibilitychange", release);
    };
  }, []);
  return (
    <GameLayout
      viewport={viewport}
      chat={chatOpen && game.auth.session ? (
        <ChatPanel chat={game.chat}
          onSelect={channel => game.chatController.select(channel)}
          onDraft={(channel, text) => game.chatController.draft(channel, text)}
          onSend={() => game.chatController.send()}
          onReading={channel => game.chatController.read(channel)}
          onClose={() => setChatOpen(false)} />
      ) : null}
      data-testid="game-shell"
      data-connection={game.status}
      data-player-id={game.profile?.playerId ?? ""}
      data-player-x={pilot?.x}
      data-player-y={pilot?.y}
      data-energy={pilot?.energy}
      data-cargo={pilot?.cargo}
      data-energy-cargo={pilot?.energyCargo}
      data-event={game.world?.event}
      data-docked={pilot?.docked}
    >
      <GameViewport
        runId={game.profile?.runId ?? ""}
        intro={game.profile ? hasIncompleteTutorial(game.profile) : false}
        tutorialGuidanceVisible={Boolean(
          pilot &&
          !docked &&
          !game.profile?.docked &&
          hasTutorialGuidance(game.profile?.tutorial ?? null),
        )}
        friends={friends}
        suspended={flightBlocked}
        eventObjective={eventNavigation}
        world={game.world}
        worldReceivedAt={game.worldReceivedAt}
        mission={mission}
        partyMarker={game.party.party?.marker ?? null}
        playerId={game.profile?.playerId ?? ""}
        boost={boost}
        targetId={targetId}
        onInput={game.sendInput}
        onTarget={setTargetId}
        onSupply={kind => game.useBattery(kind, targetId)}
        onAbility={game.ability}
        onInteract={game.interact}
        onError={game.setNotice}
        onReady={(controller) => {
          flightController.current = controller;
          setFlightReady(controller !== null);
        }}
        onNavigation={navigationChanged}
      />
      {game.world && game.profile && pilot && !docked && (
        <Hud
          cargoOpen={cargoOpen}
          onCargoOpenChange={setCargoOpen}
          hideFullscreen={platform.kind !== "standalone"}
          risk={risk}
          events={game.events}
          event={event}
          eventObjective={eventObjective}
          onJoinEvent={joinEvent}
          world={game.world}
          pilot={pilot}
          profile={game.profile}
          mission={mission}
          missions={game.missions}
          onFocusContract={game.focusContract}
          navigation={navigation}
          onNavigate={navigate}
          onCancelNavigation={() =>
            flightController.current?.cancelNavigation()
          }
          party={game.party}
          onParty={game.partyCommand}
          status={game.status}
          targetId={targetId}
          boost={boost}
          controlsBlocked={flightBlocked}
          onBoost={setBoost}
          supplyControls={game.supplyControls}
          onSupply={game.useBattery}
          onAbility={game.ability}
          onInteract={game.interact}
          onAction={game.action}
          onTarget={setTargetId}
          onAccount={openAccount}
          onHelp={openHelp}
          onChat={openChat}
          chatUnread={chatUnread}
          onOpenGalaxy={openGalaxy}
        />
      )}
      {game.world && game.profile && docked && (
        <Hangar
          contractHistory={game.contractHistory}
          achievements={game.achievements}
          events={game.events}
          onJoinEvent={joinEvent}
          profile={game.profile}
          world={game.world}
          party={game.party}
          targetId={targetId}
          connected={game.status === "connected"}
          onParty={game.partyCommand}
          onShop={() => setShopOpen(true)}
          season={<SeasonPanel view={game.season} busy={game.busy} pendingUnlock={game.pendingSeasonUnlock !== null} onReload={game.reloadSeason} onClaim={game.claimSeason} onUnlock={game.unlockSeason} onPaint={game.selectPaint} onShop={() => setShopOpen(true)} />}
          marketSupplies={onOpenHold => <SupplyPanel key={game.profile?.playerId} mode="shop" selectedSupplyId={selectedSupplyId} onSelectSupply={setSelectedSupplyId} onOpenHold={id => { setSelectedSupplyId(id); onOpenHold(); }} view={game.supplyStation} busy={game.busy} pending={game.pendingSupply !== null} recovery={game.supplyRecovery} onRecover={() => void game.recoverSupplies()}
            onBuy={game.buySupplies} onTransfer={game.transferSupplies} onRetry={game.retrySupply} onReload={() => void game.reloadSupplies()} />}
          supplies={<SupplyPanel key={game.profile.playerId} mode="hold" selectedSupplyId={selectedSupplyId} onSelectSupply={setSelectedSupplyId} view={game.supplyStation} busy={game.busy} pending={game.pendingSupply !== null} recovery={game.supplyRecovery} onRecover={() => void game.recoverSupplies()}
            onBuy={game.buySupplies} onTransfer={game.transferSupplies} onRetry={game.retrySupply} onReload={() => void game.reloadSupplies()} />}
          busy={game.busy || game.pendingSupply !== null}
          onLoadout={game.changeLoadout}
          onFitting={game.installFitting}
          onHull={game.selectHull}
          onPurchaseHull={game.purchaseHull}
          onContracts={game.chooseContracts}
          onTrainingContract={game.acceptTrainingContract}
          onCraft={game.craftEquipment}
          onDismantle={game.dismantleEquipment}
          onUpgrade={game.upgradeEquipment}
          upgradePricing={game.upgradePricing}
          pendingArsenal={game.pendingArsenal}
          onRetryArsenal={game.retryArsenal}
          onLaunch={() => {
            setTargetId("");
            setBoost(false);
            game.action("launch");
          }}
          onOpenGalaxy={openGalaxy}
          onAccount={openAccount}
          onHelp={openHelp}
          onChat={openChat}
          chatUnread={chatUnread}
        />
      )}
      {shopOpen && docked && <ShopPanel view={game.shop} receivedAt={game.shopReceivedAt} order={game.shopOrder} platform={platform} pending={game.pendingShop !== null} busy={game.shopBusy}
        onReload={game.reloadShop} onExpire={game.refreshExpiredShop} onBuy={game.buyCurrency} onCheck={game.checkShopOrder} onCancelOrder={game.closeShopOrder} onClose={() => setShopOpen(false)} />}
      {(game.auth.status === "loading" ||
        (game.auth.session &&
          (game.status === "connecting" ||
            (!pilot && game.status !== "error")))) && (
        <div className="connection-screen">
          <div className="connection-card">
            <span className="brand-symbol">✳</span>
            <span className="eyebrow">ФРОНТИР</span>
            <h1>Последний сигнал</h1>
            <p>Подключаем корабль к сектору…</p>
            <span className="loading-line" />
          </div>
        </div>
      )}
      {game.status === "error" && (
        <div className="connection-screen">
          <div className="connection-card panel">
            <span className="eyebrow">СВЯЗЬ С СЕКТОРОМ</span>
            <h1>Нужно переподключиться</h1>
            <p>{game.notice || "Нет связи с игрой."}</p>
            <button className="launch-button" onClick={game.retry}>
              Подключиться снова ↗
            </button>
          </div>
        </div>
      )}
      {game.status === "reconnecting" && (
        <div className="reconnect-banner">
          Восстанавливаем связь с кораблём…
        </div>
      )}
      {game.notice && game.status !== "error" && (
        <div className="notice" role="status">
          {game.notice}
        </div>
      )}
      {!helpOpen &&
        (accountOpen ||
          requiresFaction ||
          (!game.auth.session && game.auth.status !== "loading")) && (
          <AccountPanel
            backgroundMotion={backgroundMotion}
            onHelp={openHelp}
            profile={game.profile}
            auth={game.auth}
            busy={game.busy}
            onRename={game.renameProfile}
            onLogin={(input) => game.authController.login(input)}
            onRegister={(input) => game.authController.register(input)}
            onLogout={() => game.authController.logout()}
            onNewGuest={() => game.authController.playAsGuest()}
            onRetry={() => game.authController.retry()}
            onChooseFaction={async (factionId) => {
              const accepted = await game.authController.chooseFaction({
                factionId,
              });
              if (!accepted) setAccountOpen(true);
              return accepted;
            }}
            {...(platform.kind !== "standalone"
              ? {
                  platform: {
                    name: platform.kind === "vkgames" ? "ВКонтакте" : "VK Play",
                    ...(platform.kind === "vkplay"
                      ? { onContinue: game.continuePlatformLogin }
                      : {}),
                  },
                }
              : {})}
            {...(game.auth.session && !requiresFaction
              ? {
                  onClose: () => setAccountOpen(false),
                }
              : {})}
          />
        )}
      {helpOpen && (
        <HelpPanel
          audio={audio}
          showFullscreen={platform.kind === "standalone"}
          onClose={() => setHelpOpen(false)}
          {...(game.auth.session
            ? {
                onFeedback: () => {
                  setHelpOpen(false);
                  setFeedbackOpen(true);
                },
              }
            : {})}
        />
      )}
      {feedbackOpen && (
        <Feedback
          onSend={game.feedback}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
      {galaxyOpen && game.world && game.profile && pilot && (
        <GalaxyMap
          world={game.world}
          pilot={pilot}
          profile={game.profile}
          missions={game.missions}
          connected={game.status === "connected"}
          onClose={() => setGalaxyOpen(false)}
          initialSelection={galaxyCourse.selected}
          onSelectionChange={galaxyCourse.select}
          onNavigate={navigateFromGalaxy}
          onJoinEvent={joinEvent}
        />
      )}
    </GameLayout>
  );
}
